import axios, {
  AxiosRequestConfig,
  AxiosResponse,
  HttpStatusCode,
  InternalAxiosRequestConfig,
} from 'axios';
import { useTokenStore } from '../hooks/useStore.ts';
import MockAdapter from 'axios-mock-adapter';
import { ApiError } from '@/api/error/api.errors.ts';
import { ApiResponseCode } from '@/constants/index.ts';
import {
  HTTP_PROTOCOL,
  SEARCH_SUB_URL,
  USER_SUB_URL,
  IXIO_WEB_URL,
} from '@/constants/common';

const SEARCH_SERVICE_API = `${HTTP_PROTOCOL}://${SEARCH_SUB_URL}.${IXIO_WEB_URL}`;
const USER_SERVICE_API = `${HTTP_PROTOCOL}://${USER_SUB_URL}.${IXIO_WEB_URL}`;

export const API_ENDPOINTS = {
  search: '/v1/search/list',
  discover: '/discover',
  labTerms: '/v1/lab/terms',
  config: '/config',
  event: '/event',
  terms: '/terms',
  termsEnter: '/v1/event/enter',
};

const serviceClient = axios.create({
  baseURL: USER_SERVICE_API,
  headers: {
    'Content-Type': 'application/json',
    'Cache-Control': 'no-cache, no-store, must-revalidate',
    Pragma: 'no-cache',
    Expires: '0',
  },
});

const searchApiClient = axios.create({
  baseURL: SEARCH_SERVICE_API,
  headers: {
    'Content-Type': 'application/json',
    'Cache-Control': 'no-cache, no-store, must-revalidate',
    Pragma: 'no-cache',
    Expires: '0',
  },
});

const addBearerToken = ({ config }: { config: InternalAxiosRequestConfig }) => {
  const token = useTokenStore.getState().token;
  config.headers.Authorization = `Bearer ${token}`;

  return config;
};

searchApiClient.interceptors.request.use((config) => {
  return addBearerToken({ config });
});

serviceClient.interceptors.request.use((config) => {
  return addBearerToken({ config });
});

const apiClientMatcher = (url: string) => {
  if (
    url.startsWith(API_ENDPOINTS.search) ||
    url.startsWith(API_ENDPOINTS.labTerms)
  ) {
    return searchApiClient;
  }
  return serviceClient;
};

export interface Response<T = unknown> {
  code: string;
  message: string;
  data: T;
}

interface fetchProps extends Pick<AxiosRequestConfig, 'params' | 'headers'> {
  url: string;
  data?: unknown;
}

export const get = async <T = unknown>({
  url,
  ...config
}: Omit<fetchProps, 'data'>): Promise<AxiosResponse<Response<T>>> => {
  const apiClient = apiClientMatcher(url);
  return apiClient.get(url, config);
};

export const post = async <T = unknown>({
  url,
  data,
  ...config
}: fetchProps): Promise<AxiosResponse<Response<T>>> => {
  const apiClient = apiClientMatcher(url);
  return apiClient.post(url, data, config);
};

export const put = async <T = unknown>({
  url,
  ...config
}: fetchProps): Promise<AxiosResponse<Response<T>>> => {
  const apiClient = apiClientMatcher(url);
  return apiClient.put(url, config);
};

export const del = async <T = unknown>({
  url,
  ...config
}: fetchProps): Promise<AxiosResponse<Response<T>>> => {
  const apiClient = apiClientMatcher(url);
  return apiClient.delete(url, config);
};

export const handleApiError = (error: unknown): never => {
  if (axios.isAxiosError(error) && error.response?.data) {
    const { code, message } = error.response.data;
    throw new ApiError(code, message);
  } else if (error instanceof ApiError) {
    throw error; // 이미 처리된 ApiError는 그대로 throw
  } else {
    console.error(error);
    throw new Error('An unexpected error occurred.');
  }
};

export const handleResponse = (res: AxiosResponse) => {
  const { code, message, data } = res.data;
  if (
    res.status === HttpStatusCode.Ok &&
    res.data.code === ApiResponseCode.SUCCESS
  ) {
    return data;
  }
  throw new ApiError(code, message);
};
